import axios from 'axios'

const api = axios.create({ baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000' })

// Set by AuthProvider once MSAL is initialized
let _tokenGetter = null
export const setTokenGetter = (fn) => { _tokenGetter = fn }

// Attach a fresh Entra access token on every request
api.interceptors.request.use(async (config) => {
  if (_tokenGetter) {
    try {
      const token = await _tokenGetter()
      if (token) config.headers.Authorization = `Bearer ${token}`
    } catch {}
  }
  return config
})

// Recover from a stale cached token: if the backend rejects our token with 401,
// force a fresh token (silent MSAL refresh) and retry the request once. This
// self-heals users who would otherwise be stranded on the "unable to reach
// server" banner — no need to clear browser storage. The per-request _retried
// flag prevents any retry/redirect loop.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error
    if (response?.status === 401 && config && !config._retried && _tokenGetter) {
      config._retried = true
      try {
        const token = await _tokenGetter({ forceRefresh: true })
        if (token) return api(config)
      } catch {}
    }
    return Promise.reject(error)
  },
)

// --- Team Members ---
// Unfiltered by default, deliberately: history, past weeks and reports must
// never lose a name because somebody left. Forward-looking pickers opt in with
// { active: true } (still with the company) or { plannable: true } (still here
// AND their hours get planned). { area: <code> } narrows to one area's people.
export const getTeamMembers = (params) =>
  api.get('/api/team-members', { params }).then(r => r.data)
export const createTeamMember = (data) => api.post('/api/team-members', data).then(r => r.data)
export const updateTeamMember = (id, data) => api.put(`/api/team-members/${id}`, data).then(r => r.data)
export const deleteTeamMember = (id) => api.delete(`/api/team-members/${id}`)
export const reorderTeamMembers = (ids) => api.patch('/api/team-members/reorder', ids).then(r => r.data)
export const getRoleOptions = () => api.get('/api/team-members/role-options').then(r => r.data)

// --- Team Roles (the Cargo dropdown itself) ---
export const getTeamRoles = () => api.get('/api/team-roles').then(r => r.data)
export const createTeamRole = (data) => api.post('/api/team-roles', data).then(r => r.data)
export const updateTeamRole = (id, data) => api.put(`/api/team-roles/${id}`, data).then(r => r.data)
export const deleteTeamRole = (id) => api.delete(`/api/team-roles/${id}`)
export const reorderTeamRoles = (ids) => api.patch('/api/team-roles/reorder', ids).then(r => r.data)

// --- Unidades (Mantenimiento) ---
// Filters: { estado_actual, codigo, buscar } -- any subset, all optional.
export const getUnidades = (params) => api.get('/api/unidades', { params }).then(r => r.data)
export const getUnidad = (id) => api.get(`/api/unidades/${id}`).then(r => r.data)
export const createUnidad = (data) => api.post('/api/unidades', data).then(r => r.data)
export const updateUnidad = (id, data) => api.patch(`/api/unidades/${id}`, data).then(r => r.data)
// Keeps the unit, estado_actual -> "Baja". deleteUnidad removes it for good.
export const darDeBajaUnidad = (id) => api.post(`/api/unidades/${id}/baja`).then(r => r.data)
export const deleteUnidad = (id) => api.delete(`/api/unidades/${id}`)
// Planilla: one cell = one PUT. The first PUT with a new name creates the
// column. Encoded because names carry slashes ("A/C").
export const setUnidadAtributo = (id, nombre, valor) =>
  api.put(`/api/unidades/${id}/atributos/${encodeURIComponent(nombre)}`, { valor }).then(r => r.data)
// Catalog projects + units' projects, so a project shows up before it has units.
export const getProyectosUnidades = () => api.get('/api/atributos/proyectos').then(r => r.data)
// Columns: [{ id, nombre, proyecto (null = common), tipo, opciones }]
export const getAtributosCatalogo = (proyecto) =>
  api.get('/api/atributos/catalogo', { params: proyecto ? { proyecto } : {} }).then(r => r.data)
export const createColumna = (data) => api.post('/api/atributos/catalogo', data).then(r => r.data)
// { tipo?, opciones? } -- stored values are kept on a type change.
export const updateColumna = (id, data) => api.patch(`/api/atributos/catalogo/${id}`, data).then(r => r.data)
// Renames an estado option AND every value already saved with it.
// Resolves to { columna, valores }. 409 when `a` is another option: resend
// with fusionar = true to merge the two.
export const renombrarOpcion = (id, de, a, fusionar = false) =>
  api.post(`/api/atributos/catalogo/${id}/renombrar-opcion`, { de, a, fusionar }).then(r => r.data)
// Deletes the column's values and files too; resolves to { valores, archivos }.
export const deleteColumna = (id) => api.delete(`/api/atributos/catalogo/${id}`).then(r => r.data)
// Files of "archivo" columns. The column name goes as ?atributo= (names carry slashes).
export const uploadUnidadArchivo = (unidadId, atributo, file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post(`/api/unidades/${unidadId}/archivos`, form, { params: { atributo } }).then(r => r.data)
}
// --- Unit conversation ---
export const getComentarios = (unidadId) =>
  api.get(`/api/unidades/${unidadId}/comentarios`).then(r => r.data)
// Text, files, or both. Author and time are set by the server.
export const postComentario = (unidadId, texto, files = []) => {
  const form = new FormData()
  if (texto) form.append('texto', texto)
  files.forEach(f => form.append('archivos', f))
  return api.post(`/api/unidades/${unidadId}/comentarios`, form).then(r => r.data)
}
// A blob: URL for an attachment (thumbnails, opening). The caller revokes it.
export const comentarioArchivoUrl = async (archivo) => {
  const res = await api.get(`/api/unidades/comentarios/archivos/${archivo.id}`, { responseType: 'blob' })
  return URL.createObjectURL(new Blob([res.data], { type: archivo.mime_type || res.data.type }))
}
export const downloadComentarioArchivo = async (archivo) => {
  const res = await api.get(`/api/unidades/comentarios/archivos/${archivo.id}`, { responseType: 'blob' })
  _saveBlob(res.data, archivo.filename)
}

// --- Records with Planilla columns: units and partes share one shape ---
// Cells, column files and the conversation work the same way under
// /api/unidades and /api/partes; the Planilla pieces (ArchivosModal,
// Conversacion...) take one of these and don't care which.
const registroApi = (base) => ({
  setAtributo: (id, nombre, valor) =>
    api.put(`${base}/${id}/atributos/${encodeURIComponent(nombre)}`, { valor }).then(r => r.data),
  uploadArchivo: (id, atributo, file) => {
    const form = new FormData()
    form.append('file', file)
    return api.post(`${base}/${id}/archivos`, form, { params: { atributo } }).then(r => r.data)
  },
  deleteArchivo: (fileId) => api.delete(`${base}/archivos/${fileId}`),
  downloadArchivo: async (archivo) => {
    const res = await api.get(`${base}/archivos/${archivo.id}`, { responseType: 'blob' })
    _saveBlob(res.data, archivo.filename)
  },
  openArchivo: async (archivo) => {
    const res = await api.get(`${base}/archivos/${archivo.id}`, { responseType: 'blob' })
    const href = URL.createObjectURL(new Blob([res.data], { type: archivo.mime_type || res.data.type }))
    window.open(href, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(href), 60_000)
  },
  getComentarios: (id) => api.get(`${base}/${id}/comentarios`).then(r => r.data),
  postComentario: (id, texto, files = []) => {
    const form = new FormData()
    if (texto) form.append('texto', texto)
    files.forEach(f => form.append('archivos', f))
    return api.post(`${base}/${id}/comentarios`, form).then(r => r.data)
  },
  comentarioArchivoUrl: async (archivo) => {
    const res = await api.get(`${base}/comentarios/archivos/${archivo.id}`, { responseType: 'blob' })
    return URL.createObjectURL(new Blob([res.data], { type: archivo.mime_type || res.data.type }))
  },
  downloadComentarioArchivo: async (archivo) => {
    const res = await api.get(`${base}/comentarios/archivos/${archivo.id}`, { responseType: 'blob' })
    _saveBlob(res.data, archivo.filename)
  },
})
export const unidadesApi = registroApi('/api/unidades')
export const partesApi = registroApi('/api/partes')

// --- Partes (Mantenimiento) ---
// Filters: { estado: 'abierto'|'cerrado', unidad_id, proyecto, buscar, incluir_atributos }.
export const getPartes = (params) => api.get('/api/partes', { params }).then(r => r.data)
export const getParte = (id) => api.get(`/api/partes/${id}`).then(r => r.data)
// Fleet availability: { hoy, primer_parte, unidades, intervalos } -- see partes/disponibilidadLogica.js.
export const getDisponibilidad = () => api.get('/api/partes/disponibilidad').then(r => r.data)
// { nombre, unidad_id, fecha_apertura? }. 409 with detail.code 'parte_abierto'
// (and detail.parte) when the unit already has one open.
export const abrirParte = (data) => api.post('/api/partes', data).then(r => r.data)
export const updateParte = (id, data) => api.patch(`/api/partes/${id}`, data).then(r => r.data)
export const cancelarParte = (id, motivo) => api.post(`/api/partes/${id}/cancelar`, { motivo }).then(r => r.data)
export const cerrarParte = (id, fechaCierre) =>
  api.post(`/api/partes/${id}/cerrar`, { fecha_cierre: fechaCierre }).then(r => r.data)
export const getColumnasPartes = () =>
  api.get('/api/atributos/catalogo', { params: { ambito: 'partes' } }).then(r => r.data)
export const exportPartesExcel = async (ids) => {
  const res = await api.post('/api/partes/exportar', { ids }, { responseType: 'blob' })
  const d = new Date()
  const hoy = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  _saveBlob(res.data, `partes_${hoy}.xlsx`)
}

// --- Mantenimiento > Personas ---
// A stand-in for the company-wide people table (routers/personas_mantenimiento.py):
// when that arrives, only these calls change. Persona: { id, nombre, cargo, correo }.
// Mantenimiento area people of the register (read-only; managed in Administración).
export const getPersonasMant = (params) => api.get('/api/mantenimiento/personas', { params }).then(r => r.data)
// People of an area of the register, for the work orders: 'planta' (groups, responsables) or 'mantenimiento'.
export const getPersonasArea = (area) => api.get('/api/ot/personas', { params: { area } }).then(r => r.data)
// 409 with detail.code 'nombre_en_uso' when the name is already on the list.
export const createPersonaMant = (data) => api.post('/api/mantenimiento/personas', data).then(r => r.data)
export const updatePersonaMant = (id, data) => api.patch(`/api/mantenimiento/personas/${id}`, data).then(r => r.data)
export const deletePersonaMant = (id) => api.delete(`/api/mantenimiento/personas/${id}`)
export const exportPersonasMantExcel = async (ids) => {
  const res = await api.post('/api/mantenimiento/personas/exportar', { ids }, { responseType: 'blob' })
  const d = new Date()
  const hoy = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  _saveBlob(res.data, `personas_mantenimiento_${hoy}.xlsx`)
}

// --- Mantenimiento > Asignaciones: work capsules (routers/capsulas.py) ---
// Times are naive Chile local ISO strings. 409 detail.code: fuera_de_horario
// (retry with autorizar_hora_extra: true), ya_trabajando, parte_cerrado, solapada.
export const getTableroCapsulas = () => api.get('/api/mantenimiento/capsulas/tablero').then(r => r.data)
// { persona_id, parte_id, desde, hasta } -- dates, by the day each started.
export const getCapsulas = (params) => api.get('/api/mantenimiento/capsulas', { params }).then(r => r.data)
// { persona_id, parte_id, autorizar_hora_extra?, nota? } -> { capsula, pausada }
export const iniciarCapsula = (data) => api.post('/api/mantenimiento/capsulas/iniciar', data).then(r => r.data)
export const detenerCapsula = (id) => api.post(`/api/mantenimiento/capsulas/${id}/detener`).then(r => r.data)
// { persona_id, parte_id, inicio, fin, autorizar_hora_extra?, nota? } -> [capsula] (split normal/overtime)
export const registrarCapsula = (data) => api.post('/api/mantenimiento/capsulas/registrar', data).then(r => r.data)
export const deleteCapsula = (id) => api.delete(`/api/mantenimiento/capsulas/${id}`)
export const getHorarioLaboral = () => api.get('/api/mantenimiento/capsulas/horario').then(r => r.data)
export const putHorarioLaboral = (dias) => api.put('/api/mantenimiento/capsulas/horario', dias).then(r => r.data)
export const exportCapsulasExcel = async (filtros) => {
  const res = await api.post('/api/mantenimiento/capsulas/exportar', filtros, { responseType: 'blob' })
  _saveBlob(res.data, `capsulas_${filtros.desde}_${filtros.hasta}.xlsx`)
}

// --- Change log --- { unidad_id?, parte_id?, ambito?, proyecto?, usuario?, antes_de?, limit? }, newest first.
export const getHistorial = (params) => api.get('/api/historial', { params }).then(r => r.data)

// The chosen units' Planilla as .xlsx (the project decides which columns).
export const exportUnidadesExcel = async (ids, proyecto) => {
  const res = await api.post('/api/unidades/exportar', { ids, proyecto }, { responseType: 'blob' })
  const d = new Date()   // local date: toISOString() is UTC, a day ahead on a Chilean evening
  const hoy = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  _saveBlob(res.data, `planilla_${proyecto || 'unidades'}_${hoy}.xlsx`)
}
export const deleteUnidadArchivo = (fileId) => api.delete(`/api/unidades/archivos/${fileId}`)
// Through axios, not a plain link: the token only travels in the header.
export const downloadUnidadArchivo = async (archivo) => {
  const res = await api.get(`/api/unidades/archivos/${archivo.id}`, { responseType: 'blob' })
  _saveBlob(res.data, archivo.filename)
}
export const openUnidadArchivo = async (archivo) => {
  const res = await api.get(`/api/unidades/archivos/${archivo.id}`, { responseType: 'blob' })
  const href = URL.createObjectURL(new Blob([res.data], { type: archivo.mime_type || res.data.type }))
  window.open(href, '_blank', 'noopener')
  // Long enough for the new tab to load it.
  setTimeout(() => URL.revokeObjectURL(href), 60_000)
}

// --- Projects ---
export const getProjects = () => api.get('/api/projects').then(r => r.data)
export const getProject = (id) => api.get(`/api/projects/${id}`).then(r => r.data)
export const createProject = (data) => api.post('/api/projects', data).then(r => r.data)
export const updateProject = (id, data) => api.put(`/api/projects/${id}`, data).then(r => r.data)
export const deleteProject = (id) => api.delete(`/api/projects/${id}`)
export const reorderProjects = (ids) => api.patch('/api/projects/reorder', ids).then(r => r.data)
// The code a new project would get: "YYYY-NNN", restarting each year.
export const getNextProjectCode = (year) =>
  api.get('/api/projects/next-code', { params: year ? { year } : {} }).then(r => r.data)
// Moves the whole schedule (Gantt tasks, milestones, project dates, every
// planned staffing week) by `weeks` weeks in one step -- positive is later,
// negative is earlier.
export const shiftProjectSchedule = (id, weeks) =>
  api.post(`/api/projects/${id}/shift-schedule`, { weeks }).then(r => r.data)

// --- Milestones ---
export const getMilestones = (projectId) =>
  api.get('/api/milestones', { params: projectId ? { project_id: projectId } : {} }).then(r => r.data)
export const createMilestone = (data) => api.post('/api/milestones', data).then(r => r.data)
export const updateMilestone = (id, data) => api.put(`/api/milestones/${id}`, data).then(r => r.data)
export const deleteMilestone = (id) => api.delete(`/api/milestones/${id}`)

// --- Allocations ---
export const getAllocations = (params) => api.get('/api/allocations', { params }).then(r => r.data)
export const createAllocation = (data) => api.post('/api/allocations', data).then(r => r.data)
export const bulkUpsertAllocations = (items) => api.post('/api/allocations/bulk', items).then(r => r.data)
export const deleteAllocation = (id) => api.delete(`/api/allocations/${id}`)

// --- Time Off ---
export const getTimeOff = (params) => api.get('/api/time-off', { params }).then(r => r.data)
// Plan Week: per person, hours left that week (approved leave and holidays
// off, counted like Capacity) and that week's leave, pending included.
export const getTimeOffSemana = (start) => api.get('/api/time-off/semana', { params: { start } }).then(r => r.data)
// Days left in Buk: { disponible, saldo: { legales, dias_administrativos } }
export const getSaldoVacaciones = (personaId) => api.get(`/api/time-off/saldo/${personaId}`).then(r => r.data)
// Buk leave and holidays sync (admin)
export const getBukSyncEstado = () => api.get('/api/buk/estado').then(r => r.data)
export const sincronizarBukAusencias = () => api.post('/api/buk/sincronizar').then(r => r.data)
export const createTimeOff = (data) => api.post('/api/time-off', data).then(r => r.data)
export const updateTimeOff = (id, data) => api.put(`/api/time-off/${id}`, data).then(r => r.data)
export const deleteTimeOff = (id) => api.delete(`/api/time-off/${id}`)
// Leave requests (routers/time_off.py): anyone asks for their own; a head of
// one of their areas (or an admin) approves or rejects it.
export const requestTimeOff = (data) => api.post('/api/time-off/requests', data).then(r => r.data)
export const getMyTimeOff = () => api.get('/api/time-off/mine').then(r => r.data)
export const cancelTimeOff = (id) => api.post(`/api/time-off/${id}/cancel`).then(r => r.data)
export const getTimeOffInbox = () => api.get('/api/time-off/inbox').then(r => r.data)
export const decideTimeOff = (id, aprobar, nota) =>
  api.post(`/api/time-off/${id}/decision`, { aprobar, nota: nota || null }).then(r => r.data)

// --- Directory (routers/directorio.py): everyone, for everyone signed in ---
export const getDirectory = (params) => api.get('/api/directory', { params }).then(r => r.data)
// Birthdays in a range: [{ id, name, fecha }] -- day and month only, never the year of birth.
export const getCumpleanos = (desde, hasta) => api.get('/api/directory/cumpleanos', { params: { desde, hasta } }).then(r => r.data)
// The org chart: every active area with its superior (parent_id, null = top),
// its heads and its people count. -> [{ id, code, name, parent_id, sort_order,
// jefes: [{ id, name, role }], personas }]
export const getOrganigrama = () => api.get('/api/directory/areas').then(r => r.data)

// --- Holidays ---
export const getHolidays = (params) => api.get('/api/holidays', { params }).then(r => r.data)
// Company events (Navidad, paseo...): everyone sees them, admin and RRHH manage them.
// Contracts of the people Buk doesn't manage (admin and rrhh; c_level reads)
export const getContratos = (params) => api.get('/api/contratos', { params }).then(r => r.data)
export const getContratoOpciones = () => api.get('/api/contratos/opciones').then(r => r.data)
export const crearContrato = (body) => api.post('/api/contratos', body).then(r => r.data)
export const editarContrato = (id, body) => api.put(`/api/contratos/${id}`, body).then(r => r.data)
export const borrarContrato = (id) => api.delete(`/api/contratos/${id}`)
export const renovarContrato = (id, body) => api.post(`/api/contratos/${id}/renovaciones`, body).then(r => r.data)
export const anularRenovacion = (id, renovacionId) =>
  api.delete(`/api/contratos/${id}/renovaciones/${renovacionId}`).then(r => r.data)
export const subirArchivosContrato = (id, files) => {
  const form = new FormData()
  files.forEach(f => form.append('archivos', f))
  return api.post(`/api/contratos/${id}/archivos`, form).then(r => r.data)
}
export const borrarArchivoContrato = (archivoId) => api.delete(`/api/contratos/archivos/${archivoId}`).then(r => r.data)
// Opens the PDF in a new tab (the window first, so no popup blocker stops it).
export const abrirArchivoContrato = async (archivo) => {
  const w = window.open('', '_blank')
  try {
    const res = await api.get(`/api/contratos/archivos/${archivo.id}`, { responseType: 'blob' })
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
    if (w) w.location.href = url
    else _saveBlob(res.data, archivo.filename)
  } catch (e) {
    w?.close()
    throw e
  }
}
export const getEventos = (params) => api.get('/api/eventos', { params }).then(r => r.data)
export const crearEvento = (data) => api.post('/api/eventos', data).then(r => r.data)
export const editarEvento = (id, data) => api.put(`/api/eventos/${id}`, data).then(r => r.data)
export const borrarEvento = (id) => api.delete(`/api/eventos/${id}`)
export const createHoliday = (data) => api.post('/api/holidays', data).then(r => r.data)
export const deleteHoliday = (id) => api.delete(`/api/holidays/${id}`)
// Rename any holiday (Buk's only say "Feriado"); move a manual one.
export const updateHoliday = (id, data) => api.put(`/api/holidays/${id}`, data).then(r => r.data)
// The Monday email to everyone (admin): this week's preview, and sending it.
export const getResumenSemanal = (lunes) => api.get('/api/resumen-semanal', { params: lunes ? { lunes } : {} }).then(r => r.data)
export const enviarResumenSemanal = (data) => api.post('/api/resumen-semanal/enviar', data).then(r => r.data)

// --- Email ---
export const getEmailDefaults = () => api.get('/api/email/defaults').then(r => r.data)
export const getEmailAuthStatus = () => api.get('/api/email/auth-status').then(r => r.data)
export const sendWeekPlan = (data) => api.post('/api/email/send-plan', data).then(r => r.data)
export const getEngineerSendPreview = (weekStart) =>
  api.get('/api/email/engineer-preview', { params: { week_start: weekStart } }).then(r => r.data)
export const sendEngineerPlans = (data) =>
  api.post('/api/email/send-plan-engineers', data).then(r => r.data)
export const getPMSendPreview = (weekStart) =>
  api.get('/api/email/pm-preview', { params: { week_start: weekStart } }).then(r => r.data)
export const sendPMPlans = (data) =>
  api.post('/api/email/send-plan-pms', data).then(r => r.data)
export const sendWeekExcel = (data) =>
  api.post('/api/email/send-week-excel', data).then(r => r.data)
export const downloadWeekPlan = (weekStart) => {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:8000'
  const url = `${base}/api/email/download-plan?week_start=${weekStart}`
  const a = document.createElement('a')
  a.href = url
  a.download = `weekly_plan_${weekStart}.pdf`
  a.click()
}

// --- Sent Weeks ---
export const getSentWeeks = () => api.get('/api/sent-weeks').then(r => r.data)
export const getSentWeekAllocations = (weekStart) =>
  api.get('/api/sent-weeks/allocations', { params: { week_start: weekStart } }).then(r => r.data)
export const saveWeek = (weekStart, clearAfter = false) =>
  api.post('/api/sent-weeks', { week_start: weekStart, clear_after: clearAfter }).then(r => r.data)
export const deleteSentWeek = (id) => api.delete(`/api/sent-weeks/${id}`)
export const clearWeekAllocations = (weekStart) =>
  api.delete(`/api/allocations/week/${weekStart}`).then(r => r.data)

// --- Metrics ---
export const getMetrics = (params) => api.get('/api/metrics', { params }).then(r => r.data)

// --- Export ---
export const exportExcel = async (weekStart, weekEnd, { skipEmptyWeeks = false } = {}) => {
  const res = await api.get('/api/export/excel', {
    params: { week_start: weekStart, week_end: weekEnd, skip_empty_weeks: skipEmptyWeeks },
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `pmo_hours_${weekStart}_${weekEnd}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// --- Import ---
export const previewImport = (file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post('/api/import/excel/preview', form).then(r => r.data)
}
export const confirmImport = (payload) => api.post('/api/import/excel/confirm', payload).then(r => r.data)
export const importScheduleExcel = (weekStart, file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post(`/api/import/excel/schedule?week_start=${weekStart}`, form).then(r => r.data)
}

// --- S-Curve Export ---
export const exportSCurve = (year) => {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:8000'
  const url = `${base}/api/export/s-curve?year=${year}`
  const a = document.createElement('a')
  a.href = url
  a.download = `s_curve_${year}.xlsx`
  a.click()
}

// --- Engineer Budgets ---
export const getEngineerBudgets = (projectId, year) =>
  api.get('/api/project-engineer-budgets', { params: { project_id: projectId, year } }).then(r => r.data)
export const upsertEngineerBudget = (data) =>
  api.post('/api/project-engineer-budgets', data).then(r => r.data)
export const deleteEngineerBudget = (id) =>
  api.delete(`/api/project-engineer-budgets/${id}`)

// --- Project Materials ---
export const getMaterials = (projectId) =>
  api.get('/api/project-materials', { params: { project_id: projectId } }).then(r => r.data)
export const createMaterial = (data) =>
  api.post('/api/project-materials', data).then(r => r.data)
export const updateMaterial = (id, data) =>
  api.put(`/api/project-materials/${id}`, data).then(r => r.data)
export const deleteMaterial = (id) =>
  api.delete(`/api/project-materials/${id}`)

// --- Dashboard metrics ---
export const getPurchaseMetrics = () => api.get('/api/purchases/metrics').then(r => r.data)
export const getCrmMetrics      = () => api.get('/api/crm/metrics').then(r => r.data)
export const getCLevelMetrics   = (period = 'week') =>
  api.get('/api/metrics/c-level', { params: { period } }).then(r => r.data)

// --- Kanban Cards ---
export const getKanbanCards = (projectId) =>
  api.get('/api/kanban-cards', { params: { project_id: projectId } }).then(r => r.data)
export const createKanbanCard = (data) => api.post('/api/kanban-cards', data).then(r => r.data)
export const updateKanbanCard = (id, data) => api.put(`/api/kanban-cards/${id}`, data).then(r => r.data)
export const deleteKanbanCard = (id) => api.delete(`/api/kanban-cards/${id}`)

// --- Meeting Notes ---
export const getMeetingNotes = (projectId) =>
  api.get('/api/meeting-notes', { params: { project_id: projectId } }).then(r => r.data)
export const createMeetingNote = (data) => api.post('/api/meeting-notes', data).then(r => r.data)
export const updateMeetingNote = (id, data) => api.put(`/api/meeting-notes/${id}`, data).then(r => r.data)
export const deleteMeetingNote = (id) => api.delete(`/api/meeting-notes/${id}`)

// --- Tasks ---
export const getTasks = (projectId) =>
  api.get('/api/tasks', { params: projectId != null ? { project_id: projectId } : {} }).then(r => r.data)
export const getCrmTasks = () =>
  api.get('/api/tasks', { params: { context: 'crm' } }).then(r => r.data)
export const createTask = (data) => api.post('/api/tasks', data).then(r => r.data)
export const updateTask = (id, data) => api.put(`/api/tasks/${id}`, data).then(r => r.data)
export const deleteTask = (id) => api.delete(`/api/tasks/${id}`)
export const reorderTasks = (ids) => api.patch('/api/tasks/reorder', ids).then(r => r.data)

// --- Task Groups ---
export const getTaskGroups = (projectId) =>
  api.get('/api/task-groups', { params: projectId != null ? { project_id: projectId } : {} }).then(r => r.data)
export const getCrmTaskGroups = () =>
  api.get('/api/task-groups', { params: { context: 'crm' } }).then(r => r.data)
export const createTaskGroup = (data) => api.post('/api/task-groups', data).then(r => r.data)
export const updateTaskGroup = (id, data) => api.put(`/api/task-groups/${id}`, data).then(r => r.data)
export const deleteTaskGroup = (id) => api.delete(`/api/task-groups/${id}`)

// --- Gantt ---
// One aggregate read: the axis range depends on the rows and the holidays
// depend on the axis, so composing this from /api/tasks + /api/milestones +
// /api/holidays in the browser would be a request waterfall.
export const getProjectGantt = (projectId) =>
  api.get(`/api/projects/${projectId}/gantt`).then(r => r.data)

export const reorderMilestones = (ids) =>
  api.patch('/api/milestones/reorder', ids).then(r => r.data)

export const previewGanttImport = (projectId, file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post(`/api/projects/${projectId}/gantt/import/preview`, form).then(r => r.data)
}

export const confirmGanttImport = (projectId, raw, deleteMissing = false) =>
  api.post(`/api/projects/${projectId}/gantt/import/confirm`,
    { raw, delete_missing: deleteMissing }).then(r => r.data)

// Both downloads go through axios for the same reason as
// exportManagementReport below: the token only lives in the Authorization
// header, which a plain <a href> navigation cannot set.
const _saveBlob = (blob, filename) => {
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(href)
}

const downloadXlsx = async (url, filename) => {
  const res = await api.get(url, { responseType: 'blob' })
  _saveBlob(res.data, filename)
}

export const downloadGanttTemplate = (projectId, projectName = 'proyecto') =>
  downloadXlsx(`/api/projects/${projectId}/gantt/template.xlsx`,
    `gantt_plantilla_${projectName}.xlsx`)

export const exportGantt = (projectId, projectName = 'proyecto') =>
  downloadXlsx(`/api/projects/${projectId}/gantt/export.xlsx`,
    `carta_gantt_${projectName}.xlsx`)

// --- System ---
export const downloadBackup = async () => {
  const res = await api.get('/api/system/backup', { responseType: 'blob' })
  const disposition = res.headers['content-disposition'] || ''
  const match = disposition.match(/filename="?([^"]+)"?/)
  const filename = match ? match[1] : 'pmo-backup.db'
  _saveBlob(res.data, filename)
  return filename
}
// Replaces the whole database with a backup; confirmar must be "RESTAURAR".
export const restoreBackup = (archivo, confirmar) => {
  const form = new FormData()
  form.append('archivo', archivo)
  form.append('confirmar', confirmar)
  return api.post('/api/system/restore', form).then(r => r.data)
}
// monday.com board export -> Mantenimiento. aplicar=false is a preview:
// the server loads it, summarises, and rolls back.
export const importarMonday = (tipo, archivo, { reemplazar = false, aplicar = false } = {}) => {
  const form = new FormData()
  form.append('archivo', archivo)
  form.append('reemplazar', reemplazar)
  form.append('aplicar', aplicar)
  return api.post(`/api/system/importar/${tipo}`, form).then(r => r.data)
}

// --- API keys (personal, for connecting an external agent/script) ---
// --- Staffing scenarios (c_level only) ---
export const listScenarios = () => api.get('/api/scenarios').then(r => r.data)
export const createScenario = (name, notes = null) =>
  api.post('/api/scenarios', { name, notes }).then(r => r.data)
export const getScenario = (id) => api.get(`/api/scenarios/${id}`).then(r => r.data)
export const deleteScenario = (id) => api.delete(`/api/scenarios/${id}`)
export const refreshScenario = (id) => api.post(`/api/scenarios/${id}/refresh`).then(r => r.data)
export const addScenarioFireAction = (scenarioId, { teamMemberId, fireDate, replacementRole }) =>
  api.post(`/api/scenarios/${scenarioId}/fire-actions`, {
    team_member_id: teamMemberId, fire_date: fireDate, replacement_role: replacementRole,
  }).then(r => r.data)
export const createScenarioProject = (scenarioId, data) =>
  api.post(`/api/scenarios/${scenarioId}/projects`, data).then(r => r.data)
export const updateScenarioProject = (scenarioId, projectId, data) =>
  api.put(`/api/scenarios/${scenarioId}/projects/${projectId}`, data).then(r => r.data)
export const deleteScenarioProject = (scenarioId, projectId) =>
  api.delete(`/api/scenarios/${scenarioId}/projects/${projectId}`)
export const addScenarioProjectRow = (scenarioId, projectId, data) =>
  api.post(`/api/scenarios/${scenarioId}/projects/${projectId}/rows`, data).then(r => r.data)
export const deleteScenarioProjectRow = (scenarioId, projectId, rowId) =>
  api.delete(`/api/scenarios/${scenarioId}/projects/${projectId}/rows/${rowId}`)
export const removeScenarioFireAction = (scenarioId, actionId) =>
  api.delete(`/api/scenarios/${scenarioId}/fire-actions/${actionId}`).then(r => r.data)

export const listApiKeys = () => api.get('/api/api-keys').then(r => r.data)
export const createApiKey = (name, expiresInDays = null) =>
  api.post('/api/api-keys', { name, expires_in_days: expiresInDays }).then(r => r.data)
export const revokeApiKey = (id) => api.delete(`/api/api-keys/${id}`)

// --- Budgets ---
// Hours come from weekly_allocations, money from the CLP twins of the purchase
// orders. Both sides are always in the same shape: budget / used / remaining.
export const getProjectBudget = (projectId) =>
  api.get(`/api/projects/${projectId}/budget`).then(r => r.data)

export const setProjectBudget = (projectId, data) =>
  api.put(`/api/projects/${projectId}/budget`, data).then(r => r.data)

export const resyncProjectBudget = (projectId) =>
  api.post(`/api/projects/${projectId}/budget/resync`).then(r => r.data)

export const getPortfolioBudgets = () =>
  api.get('/api/management/budgets').then(r => r.data)

// What approving a purchase would leave in its project's budget. Read-only,
// and gated exactly like reading the order itself.
export const getPurchaseBudgetImpact = (purchaseId) =>
  api.get(`/api/purchases/${purchaseId}/budget-impact`).then(r => r.data)

// --- Staffing (planned hours per person per week) ---
export const getProjectStaffing = (projectId, scenarioId) =>
  api.get(`/api/projects/${projectId}/staffing`, { params: { scenario_id: scenarioId || undefined } }).then(r => r.data)
export const addStaffingRow = (projectId, data) =>
  api.post(`/api/projects/${projectId}/staffing/rows`, data).then(r => r.data)
export const updateStaffingRow = (projectId, rowId, data) =>
  api.put(`/api/projects/${projectId}/staffing/rows/${rowId}`, data).then(r => r.data)
export const deleteStaffingRow = (projectId, rowId) =>
  api.delete(`/api/projects/${projectId}/staffing/rows/${rowId}`)
export const setStaffingWeeks = (projectId, rowId, data, scenarioId) =>
  api.put(`/api/projects/${projectId}/staffing/rows/${rowId}/weeks`, data,
    { params: { scenario_id: scenarioId || undefined } }).then(r => r.data)
// Everything the alert rules flagged. The bulk call is one pass over the
// whole portfolio, so the projects list stays the same speed however many
// rules get added; the per-project one feeds that project's own banners.
export const getProjectAlertsBulk = () =>
  api.get('/api/project-alerts').then(r => r.data)

export const getProjectAlerts = (projectId) =>
  api.get(`/api/projects/${projectId}/alerts`).then(r => r.data.alerts)

// Hand a stretch of one person's commitment to somebody else. Omit the weeks
// for "everything from here on" (somebody left); pass them for a holiday.
export const replaceStaffingRow = (projectId, rowId, data) =>
  api.post(`/api/projects/${projectId}/staffing/rows/${rowId}/replace`, data)
    .then(r => r.data)

export const applyStaffingToBudget = (projectId) =>
  api.post(`/api/projects/${projectId}/staffing/apply-to-budget`).then(r => r.data)

// Copies whatever Plan Week already logged onto weeks the plan never
// covered at the time -- the tool for backfilling a month the project only
// started tracking after the fact, so its hh-presupuesto counts that month.
export const previewStaffingBackfill = (projectId) =>
  api.get(`/api/projects/${projectId}/staffing/backfill/preview`).then(r => r.data)
export const confirmStaffingBackfill = (projectId) =>
  api.post(`/api/projects/${projectId}/staffing/backfill/confirm`).then(r => r.data)

// Bulk load, same three steps as the Gantt: the template comes down carrying
// the roster and the week columns, so the spreadsheet is only ever for the
// hours -- people are put on the project in the app.
export const downloadStaffingTemplate = (projectId, projectName = 'proyecto') =>
  downloadXlsx(`/api/projects/${projectId}/staffing/template.xlsx`,
    `dotacion_${projectName}.xlsx`)

export const previewStaffingImport = (projectId, file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post(`/api/projects/${projectId}/staffing/import/preview`, form)
    .then(r => r.data)
}

export const confirmStaffingImport = (projectId, raw) =>
  api.post(`/api/projects/${projectId}/staffing/import/confirm`, { raw })
    .then(r => r.data)

// --- Exchange rates (budgets only; purchases keep their own currency) ---
export const getFxRates = () => api.get('/api/fx/rates').then(r => r.data)
export const setFxRate = (data) => api.put('/api/fx/rates', data).then(r => r.data)
export const refreshFxRates = () => api.post('/api/fx/rates/refresh').then(r => r.data)
export const backfillPurchaseProjects = (apply = false) =>
  api.post('/api/purchases/backfill-projects', { apply }).then(r => r.data)

// --- Project Budgets ---
export const importBudgetExcel = (file, year = 2026) => {
  const form = new FormData()
  form.append('file', file)
  return api.post(`/api/project-budgets/import-excel?year=${year}`, form).then(r => r.data)
}

// --- Project weekly hour limits (min/max hours per week, per month) ---
export const getProjectWeeklyLimits = (projectId, year) =>
  api.get('/api/project-weekly-limits', { params: { project_id: projectId, year } })
    .then(r => r.data)
export const upsertProjectWeeklyLimit = (data) =>
  api.post('/api/project-weekly-limits', data).then(r => r.data)

// --- Management ---
export const getManagementHours = (period, sCurveOnly = false) =>
  api.get('/api/management/hours', { params: { period, s_curve_only: sCurveOnly } })
    .then(r => r.data)

export const getHourLimits = (year) =>
  api.get('/api/management/hour-limits', { params: { year } }).then(r => r.data)

// The weekly rates in force for the week being planned. Plan Week already holds
// that week's hours in its own state, so it warns live as cells are edited.
export const getWeekLimitUsage = (weekStart) =>
  api.get('/api/management/week-limit-usage', { params: { week_start: weekStart } })
    .then(r => r.data)

// The project's LIFETIME hours budget and what other weeks already took, so
// Plan Week can also warn about a project running out of hours altogether --
// a different question from whether this week sits inside its weekly band.
export const getWeekBudgetUsage = (weekStart) =>
  api.get('/api/management/week-budget-usage', { params: { week_start: weekStart } })
    .then(r => r.data)

// Fills one min/max weekly rate across all twelve months of a project-year. A
// null field is left untouched; onlyEmpty keeps months that already have a value.
export const bulkUpsertWeeklyLimits = ({ projectId, year, minWeeklyHours, maxWeeklyHours, onlyEmpty = true }) =>
  api.post('/api/management/weekly-limits/bulk', {
    project_id: projectId, year,
    min_weekly_hours: minWeeklyHours, max_weekly_hours: maxWeeklyHours,
    only_empty: onlyEmpty,
  }).then(r => r.data)

export const setProjectTotalLimit = (projectId, totalHourLimit) =>
  api.post('/api/management/project-total-limit', {
    project_id: projectId, total_hour_limit: totalHourLimit,
  }).then(r => r.data)

// The limits grid as a spreadsheet, pre-filled with what is already stored —
// the same file goes back through importHourLimits below.
export const downloadHourLimitsTemplate = async (year) => {
  const res = await api.get('/api/management/hour-limits/template.xlsx', {
    params: { year }, responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `hour_limits_${year}.xlsx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const importHourLimits = (year, file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post('/api/management/hour-limits/import', form, { params: { year } })
    .then(r => r.data)
}

// Downloads through axios rather than an <a href> click: the export router is
// behind Depends(get_current_user) and the token only travels in the
// Authorization header, which a plain anchor navigation cannot set.
export const exportManagementReport = async (period, sCurveOnly = false) => {
  const res = await api.get('/api/export/management-report', {
    params: { period, s_curve_only: sCurveOnly },
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `management_report_${period}_${new Date().toISOString().slice(0, 10)}.xlsx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// --- S-Curve hours (bar chart) ---
export const getSCurveHours = (window = 'ytd') =>
  api.get('/api/scurve-hours', { params: { window } }).then(r => r.data)

// --- Users (admin only) ---
export const getUsers = () => api.get('/api/users').then(r => r.data)
export const updateUser = (id, data) => api.put(`/api/users/${id}`, data).then(r => r.data)
export const deleteUser = (id) => api.delete(`/api/users/${id}`)

// --- Administración (admin only): the company's people, areas, accounts ---
// People: { area, q, include_inactive } -- any subset. Each comes with its
// areas and its account (cuenta, or null).
export const getAdminPeople = (params) => api.get('/api/admin/people', { params }).then(r => r.data)
// { ..., areas: [{ area_id, es_jefe }], cuenta?: { roles } } -- cuenta makes
// their login account now (needs an @rebornelectric.cl email).
export const createAdminPerson = (data) => api.post('/api/admin/people', data).then(r => r.data)
// HR's nómina (.xlsx) -> the register. aplicar=false is a preview: the
// server does it all, summarises, and rolls back.
// -> { aplicado, filas, resumen: { actualizadas, creadas, sin_cambio, ambiguas,
//      conflictos, errores, no_en_nomina } }
// Buk (HR's API) as the nómina: same preview / apply and summary.
export const getBukEstado = () => api.get('/api/admin/people/buk').then(r => r.data)
export const sincronizarBuk = (aplicar = false) =>
  api.post('/api/admin/people/sincronizar-buk', { aplicar }).then(r => r.data)

export const importarNomina = (archivo, aplicar = false) => {
  const form = new FormData()
  form.append('archivo', archivo)
  form.append('aplicar', aplicar)
  return api.post('/api/admin/people/importar-nomina', form).then(r => r.data)
}
// Any subset of the fields; `areas` replaces the whole set.
export const updateAdminPerson = (id, data) => api.put(`/api/admin/people/${id}`, data).then(r => r.data)
// 409 tiene_capsulas: they have recorded work -- deactivate them instead.
export const deleteAdminPerson = (id) => api.delete(`/api/admin/people/${id}`)
export const createAdminAccountFor = (id, roles) =>
  api.post(`/api/admin/people/${id}/cuenta`, { roles }).then(r => r.data)
// { personas: [{ motivo, clave, personas }], cuentas: [{ cuenta, personas }] }
export const getAdminDuplicates = () => api.get('/api/admin/people/duplicados').then(r => r.data)
// Merge source into target. dry_run only reports; the real one deletes the
// source and can't be undone. -> { ok, conflictos, movidas, campos }
export const mergeAdminPeople = (targetId, sourceId, dryRun = true) =>
  api.post(`/api/admin/people/${targetId}/fusionar`, { source_id: sourceId, dry_run: dryRun }).then(r => r.data)
// { email, display_name, roles, team_member_id? }
export const createAdminAccount = (data) => api.post('/api/admin/cuentas', data).then(r => r.data)
// team_member_id null unlinks. One account per person (409 otherwise).
export const linkAdminAccount = (userId, teamMemberId) =>
  api.put(`/api/admin/cuentas/${userId}/vinculo`, { team_member_id: teamMemberId }).then(r => r.data)
export const getAdminAreas = () => api.get('/api/admin/areas').then(r => r.data)
// { name, parent_id?, sort_order?, is_active } -- parent_id: the area it
// reports to (null = top). 422 if it would make a cycle.
export const createAdminArea = (data) => api.post('/api/admin/areas', data).then(r => r.data)
// { name, parent_id, sort_order?, is_active } -- the code never changes.
// parent_id is always replaced (omitted = top level), so send it every time.
// DELETE: 409 if the area has people or areas below it.
export const updateAdminArea = (id, data) => api.put(`/api/admin/areas/${id}`, data).then(r => r.data)
export const deleteAdminArea = (id) => api.delete(`/api/admin/areas/${id}`)
// Administración > Proyectos: the real projects with their areas and units.
// params: { area: <area code>, include_inactive: true|false }
export const getAdminProjects = (params) => api.get('/api/admin/projects', { params }).then(r => r.data)
export const setAdminProjectAreas = (id, areaIds) =>
  api.put(`/api/admin/projects/${id}/areas`, { area_ids: areaIds }).then(r => r.data)

// --- Purchases / Acquisitions ---
export const getPurchases = (params = { scope: 'mine' }) =>
  api.get('/api/purchases', { params }).then(r => r.data)
export const getPurchase = (id) =>
  api.get(`/api/purchases/${id}`).then(r => r.data)
export const createPurchase = (data) =>
  api.post('/api/purchases', data).then(r => r.data)
export const updatePurchase = (id, data) =>
  api.patch(`/api/purchases/${id}`, data).then(r => r.data)
export const deletePurchase = (id) =>
  api.delete(`/api/purchases/${id}`)
export const submitPurchase = (id) =>
  api.post(`/api/purchases/${id}/submit`).then(r => r.data)
export const approvePurchase = (id) =>
  api.post(`/api/purchases/${id}/approve`).then(r => r.data)
export const rejectPurchase = (id, reason) =>
  api.post(`/api/purchases/${id}/reject`, { reason }).then(r => r.data)
export const cancelPurchase = (id, reason) =>
  api.post(`/api/purchases/${id}/cancel`, { reason }).then(r => r.data)
export const sapPurchase = (id, po_number) =>
  api.post(`/api/purchases/${id}/sap`, { po_number }).then(r => r.data)
export const orderPurchase = (id, data = {}) =>
  api.post(`/api/purchases/${id}/order`, data).then(r => r.data)
export const receivePurchase = (id) =>
  api.post(`/api/purchases/${id}/receive`).then(r => r.data)
export const closePurchase = (id) =>
  api.post(`/api/purchases/${id}/close`).then(r => r.data)
export const updatePurchaseOrderFields = (id, data) =>
  api.patch(`/api/purchases/${id}/order-fields`, data).then(r => r.data)
// The queue holds requests and SAP orders together: `items` is the whole order, [{ tipo: 'solicitud' | 'pedido', id }].
export const reorderPurchasePriority = (items, cutAfter) =>
  api.post('/api/purchases/priority/reorder', { ordered_items: items, ...(cutAfter === undefined ? {} : { cut_after: cutAfter }) }).then(r => r.data)
export const getPrioridadPedidos = () => api.get('/api/purchases/priority/pedidos').then(r => r.data)
export const getPrioridadPedidosCandidatos = (q) =>
  api.get('/api/purchases/priority/pedidos/candidatos', { params: q ? { q } : {} }).then(r => r.data)
export const agregarPedidoPrioridad = (pedido_id) => api.post('/api/purchases/priority/pedidos', { pedido_id }).then(r => r.data)
export const quitarPedidoPrioridad = (pedido_id) => api.delete(`/api/purchases/priority/pedidos/${pedido_id}`)
export const getPurchasePriorityCut = () =>
  api.get('/api/purchases/priority/cut').then(r => r.data)
export const getPurchaseEdits = (id) =>
  api.get(`/api/purchases/${id}/edits`).then(r => r.data)
export const uploadPurchaseAttachment = (id, file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post(`/api/purchases/${id}/attachments`, form).then(r => r.data)
}
export const downloadPurchaseAttachment = async (reqId, att) => {
  const res = await api.get(`/api/purchases/${reqId}/attachments/${att.id}`, {
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = att.filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
export const deletePurchaseAttachment = (reqId, attId) =>
  api.delete(`/api/purchases/${reqId}/attachments/${attId}`)
export const exportPurchasesExcel = async (params = {}) => {
  const res = await api.get('/api/purchases/export.xlsx', {
    params, paramsSerializer: { indexes: null },     // ids go as repeated keys
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  const stamp = new Date().toISOString().slice(0, 10)
  a.download = `purchases_${stamp}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
export const exportPurchaseExcel = async (reqId) => {
  const res = await api.get(`/api/purchases/${reqId}/export.xlsx`, { responseType: 'blob' })
  const dispo = res.headers?.['content-disposition'] || ''
  const m = /filename="([^"]+)"/.exec(dispo)
  const fname = m ? m[1] : `purchase_${reqId}.xlsx`
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = fname
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
export const getPurchaseComments = (id) =>
  api.get(`/api/purchases/${id}/comments`).then(r => r.data)
export const addPurchaseComment = (id, payload) =>
  api.post(`/api/purchases/${id}/comments`, payload).then(r => r.data)
export const getTaggableUsers = () =>
  api.get('/api/purchases/taggable-users').then(r => r.data)

// --- CRM ---
export const getLeads = () => api.get('/api/crm/leads').then(r => r.data)
export const createLead = (data) => api.post('/api/crm/leads', data).then(r => r.data)
export const updateLead = (id, data) => api.patch(`/api/crm/leads/${id}`, data).then(r => r.data)
export const deleteLead = (id) => api.delete(`/api/crm/leads/${id}`)
export const addLeadActivity = (id, data) => api.post(`/api/crm/leads/${id}/activities`, data).then(r => r.data)
export const deleteLeadActivity = (actId) => api.delete(`/api/crm/activities/${actId}`)

// --- Fabrication teams (the warehouse board's team picker) ---
export const getFabricationTeams = () =>
  api.get('/api/fabrication-teams').then(r => r.data)

// --- Operaciones > Planta: Órdenes de trabajo (routers/ot.py) ---
// What the current user may do (their functions) and each OT type's states.
export const getOtConfig = () => api.get('/api/ot/config').then(r => r.data)
// Filters: { tipo, estado, origen, proyecto_id, buscar, mias }.
export const getOts = (params) => api.get('/api/ot/ordenes', { params }).then(r => r.data)
export const getOt = (id) => api.get(`/api/ot/ordenes/${id}`).then(r => r.data)
// Creates a draft (borrador).
export const createOt = (data) => api.post('/api/ot/ordenes', data).then(r => r.data)
// OT batches (L-<n>): many items created and admitted together.
export const revisarLote = (data) => api.post('/api/ot/lotes/revisar', data).then(r => r.data)
export const leerExcelLote = (file) => {
  const fd = new FormData()
  fd.append('archivo', file)
  return api.post('/api/ot/lotes/leer-excel', fd).then(r => r.data)
}
export const crearLote = (data) => api.post('/api/ot/lotes', data).then(r => r.data)
export const getLote = (id) => api.get(`/api/ot/lotes/${id}`).then(r => r.data)
export const accionLote = (id, accion, data) => api.post(`/api/ot/lotes/${id}/acciones/${accion}`, data).then(r => r.data)
export const crearOtServicio = (data) => api.post('/api/ot/servicios', data).then(r => r.data)
export const getCategoriasServicio = (params) => api.get('/api/ot/categorias-servicio', { params }).then(r => r.data)
export const updateOt = (id, data) => api.patch(`/api/ot/ordenes/${id}`, data).then(r => r.data)
export const deleteOt = (id) => api.delete(`/api/ot/ordenes/${id}`)
// A step of the flow (enviar, proponer, ...). 422 detail.code 'validacion'
// with detail.faltan when something required is missing; 403 without the function.
export const accionOt = (id, accion, data = {}) =>
  api.post(`/api/ot/ordenes/${id}/acciones/${accion}`, data).then(r => r.data)
// tipo: bom | plano | pdf_of | foto | otro
export const uploadOtAdjunto = (id, tipo, file) => {
  const form = new FormData()
  form.append('file', file)
  form.append('tipo', tipo)
  return api.post(`/api/ot/ordenes/${id}/adjuntos`, form).then(r => r.data)
}
export const downloadOtAdjunto = async (otId, adj) => {
  const res = await api.get(`/api/ot/ordenes/${otId}/adjuntos/${adj.id}`, { responseType: 'blob' })
  _saveBlob(res.data, adj.filename)
}
export const deleteOtAdjunto = (otId, adjId) => api.delete(`/api/ot/ordenes/${otId}/adjuntos/${adjId}`)
// The OT's conversation: notes with files, from anyone who can open the OT.
export const getOtMensajes = (otId) => api.get(`/api/ot/ordenes/${otId}/mensajes`).then(r => r.data)
export const escribirOtMensaje = (otId, texto, archivos = []) => {
  const form = new FormData()
  if (texto) form.append('texto', texto)
  archivos.forEach(f => form.append('archivos', f))
  return api.post(`/api/ot/ordenes/${otId}/mensajes`, form).then(r => r.data)
}
export const downloadOtMensajeAdjunto = async (otId, mensajeId, adj) => {
  const res = await api.get(`/api/ot/ordenes/${otId}/mensajes/${mensajeId}/adjuntos/${adj.id}`, { responseType: 'blob' })
  _saveBlob(res.data, adj.filename)
}
// An image of the conversation as an object URL (the request carries the auth header).
export const verOtMensajeAdjunto = async (otId, mensajeId, adj) => {
  const res = await api.get(`/api/ot/ordenes/${otId}/mensajes/${mensajeId}/adjuntos/${adj.id}`, { params: { inline: true }, responseType: 'blob' })
  return URL.createObjectURL(res.data)
}
export const exportOtsExcel = async (ids) => {
  const res = await api.post('/api/ot/exportar', { ids }, { responseType: 'blob' })
  const d = new Date()
  const hoy = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  _saveBlob(res.data, `ordenes_trabajo_${hoy}.xlsx`)
}
// The SAP queue: [{ tarea, ot_id, ot_codigo, articulo_*, cantidad, puede_ejecutar, ... }].
// estado: abiertas (default) | pendiente | bloqueada | confirmada | rechazada | todas
export const getColaSap = (estado) => api.get('/api/ot/cola-sap', { params: { estado } }).then(r => r.data)
// Done in SAP. The OF (crear_liberar_of) needs folio and its PDF -> the OT is released. Answers the OT.
// forzar + motivo_diferencia: confirm an OF that doesn't match the OT (logged).
export const confirmarTareaSap = (id, { folio, nota, archivo, forzar, motivo_diferencia }) => {
  const form = new FormData()
  if (folio) form.append('folio', folio)
  if (nota) form.append('nota', nota)
  if (archivo) form.append('archivo', archivo)
  if (forzar) form.append('forzar', 'true')
  if (motivo_diferencia) form.append('motivo_diferencia', motivo_diferencia)
  return api.post(`/api/ot/tareas-sap/${id}/confirmar`, form).then(r => r.data)
}
// The validation step: the OF's PDF read and compared with the OT -> { datos, checks, coincide, folio_en_uso }.
export const leerOfPdf = (id, archivo) => {
  const form = new FormData()
  form.append('archivo', archivo)
  return api.post(`/api/ot/tareas-sap/${id}/leer-of`, form).then(r => r.data)
}
// devolver: while the OT is planned, send it back to its requester (default) instead of leaving the task rejected.
export const rechazarTareaSap = (id, motivo, devolver = true) =>
  api.post(`/api/ot/tareas-sap/${id}/rechazar`, { motivo, devolver }).then(r => r.data)
export const reabrirTareaSap = (id) => api.post(`/api/ot/tareas-sap/${id}/reabrir`).then(r => r.data)
// A task rejected earlier and kept: send its OT back to the requester after all.
export const devolverTareaSap = (id, motivo) => api.post(`/api/ot/tareas-sap/${id}/devolver`, motivo ? { motivo } : {}).then(r => r.data)
// Work groups: { id, nombre, activo, supervisor, miembros: [{ id, nombre }], n_ots }.
export const getGruposPlanta = (params) => api.get('/api/ot/grupos', { params }).then(r => r.data)
export const createGrupoPlanta = (data) => api.post('/api/ot/grupos', data).then(r => r.data)
// { nombre?, supervisor_id?, activo?, miembros?: [persona ids] }
export const updateGrupoPlanta = (id, data) => api.patch(`/api/ot/grupos/${id}`, data).then(r => r.data)
export const deleteGrupoPlanta = (id) => api.delete(`/api/ot/grupos/${id}`)
// Who holds each function: [{ codigo, descripcion, asignaciones: [{ id, rol | usuario_id, ... }] }].
export const getPermisosOt = () => api.get('/api/ot/permisos').then(r => r.data)
export const getUsuariosPermisos = () => api.get('/api/ot/permisos/usuarios').then(r => r.data)
export const getPortadoresPermisos = () => api.get('/api/ot/permisos/portadores').then(r => r.data)
export const getRolesPermisos = () => api.get('/api/ot/permisos/roles').then(r => r.data)
export const getPersonasConFuncion = (funcion) => api.get(`/api/ot/permisos/${encodeURIComponent(funcion)}/personas`).then(r => r.data)
// { funcion_codigo, rol | usuario_id, fecha_revision?, nota? }
export const createPermisoOt = (data) => api.post('/api/ot/permisos', data).then(r => r.data)
export const updatePermisoOt = (id, data) => api.patch(`/api/ot/permisos/${id}`, data).then(r => r.data)
export const deletePermisoOt = (id) => api.delete(`/api/ot/permisos/${id}`)

// --- Comercial ---
// Its own permissions (backend comercial_permisos.py), apart from the OT's above.
export const getPermisosComercial = () => api.get('/api/comercial/permisos').then(r => r.data)
export const getPermisosComercialUsuarios = () => api.get('/api/comercial/permisos/usuarios').then(r => r.data)
export const getPermisosComercialPortadores = () => api.get('/api/comercial/permisos/portadores').then(r => r.data)
export const getPermisosComercialRoles = () => api.get('/api/comercial/permisos/roles').then(r => r.data)
export const getPersonasConFuncionComercial = (funcion) =>
  api.get(`/api/comercial/permisos/${encodeURIComponent(funcion)}/personas`).then(r => r.data)
// { funcion_codigo, rol | usuario_id | persona_id | cargo | area | jefe_de_area, fecha_revision?, nota? }
export const createPermisoComercial = (data) => api.post('/api/comercial/permisos', data).then(r => r.data)
export const updatePermisoComercial = (id, data) => api.patch(`/api/comercial/permisos/${id}`, data).then(r => r.data)
export const deletePermisoComercial = (id) => api.delete(`/api/comercial/permisos/${id}`)
// Organizations (backend comercial_organizaciones.py). Listing params:
// { q, tipo, pais, superior, archivadas: 'no' | 'si' | 'todas' }. Saving sends
// confirmar_duplicados: true to keep it despite a 409 posibles_duplicados.
export const getOrganizacionesComercial = (params) => api.get('/api/comercial/organizaciones', { params }).then(r => r.data)
export const getArbolOrganizacionesComercial = (params) => api.get('/api/comercial/organizaciones/arbol', { params }).then(r => r.data)
export const getOrganizacionComercial = (id) => api.get(`/api/comercial/organizaciones/${id}`).then(r => r.data)
export const createOrganizacionComercial = (data) => api.post('/api/comercial/organizaciones', data).then(r => r.data)
export const updateOrganizacionComercial = (id, data) => api.patch(`/api/comercial/organizaciones/${id}`, data).then(r => r.data)
export const deleteOrganizacionComercial = (id) => api.delete(`/api/comercial/organizaciones/${id}`)
export const archivarOrganizacionComercial = (id) => api.post(`/api/comercial/organizaciones/${id}/archivar`).then(r => r.data)
export const restaurarOrganizacionComercial = (id) => api.post(`/api/comercial/organizaciones/${id}/restaurar`).then(r => r.data)
export const getAscendientesOrganizacionComercial = (id) => api.get(`/api/comercial/organizaciones/${id}/ascendientes`).then(r => r.data)
export const getDescendientesOrganizacionComercial = (id, params) =>
  api.get(`/api/comercial/organizaciones/${id}/descendientes`, { params }).then(r => r.data)
// { subordinadas, inactivos, archivados }: one row per contact, with their links in that subtree.
export const getContactosDeOrganizacionComercial = (id, params) =>
  api.get(`/api/comercial/organizaciones/${id}/contactos`, { params }).then(r => r.data)
// Organization types: { activos } to filter; { nombre, activo, orden }.
export const getTiposOrganizacionComercial = (params) => api.get('/api/comercial/tipos', { params }).then(r => r.data)
export const createTipoOrganizacionComercial = (data) => api.post('/api/comercial/tipos', data).then(r => r.data)
export const updateTipoOrganizacionComercial = (id, data) => api.patch(`/api/comercial/tipos/${id}`, data).then(r => r.data)
export const deleteTipoOrganizacionComercial = (id) => api.delete(`/api/comercial/tipos/${id}`)
// Contacts (backend comercial_contactos.py). Listing params:
// { q, organizacion, subordinadas, etiqueta, archivados: 'no' | 'si' | 'todos', inactivos }.
export const getContactosComercial = (params) => api.get('/api/comercial/contactos', { params }).then(r => r.data)
export const getContactoComercial = (id, params) => api.get(`/api/comercial/contactos/${id}`, { params }).then(r => r.data)
export const createContactoComercial = (data) => api.post('/api/comercial/contactos', data).then(r => r.data)
export const updateContactoComercial = (id, data) => api.patch(`/api/comercial/contactos/${id}`, data).then(r => r.data)
export const deleteContactoComercial = (id) => api.delete(`/api/comercial/contactos/${id}`)
export const archivarContactoComercial = (id) => api.post(`/api/comercial/contactos/${id}/archivar`).then(r => r.data)
export const restaurarContactoComercial = (id) => api.post(`/api/comercial/contactos/${id}/restaurar`).then(r => r.data)
// Links contact-organization: { organizacion_id, cargo, area, activo }; activo=false when they left the position.
export const createVinculoComercial = (contactoId, data) =>
  api.post(`/api/comercial/contactos/${contactoId}/vinculos`, data).then(r => r.data)
export const updateVinculoComercial = (id, data) => api.patch(`/api/comercial/vinculos/${id}`, data).then(r => r.data)
export const deleteVinculoComercial = (id) => api.delete(`/api/comercial/vinculos/${id}`)
// Contact labels: { activas } to filter; { nombre, activo, orden }.
export const getEtiquetasContactoComercial = (params) => api.get('/api/comercial/etiquetas', { params }).then(r => r.data)
export const createEtiquetaContactoComercial = (data) => api.post('/api/comercial/etiquetas', data).then(r => r.data)
export const updateEtiquetaContactoComercial = (id, data) => api.patch(`/api/comercial/etiquetas/${id}`, data).then(r => r.data)
export const deleteEtiquetaContactoComercial = (id) => api.delete(`/api/comercial/etiquetas/${id}`)

// --- Operations / Kanban ---
export const getOperationsBoards = () =>
  api.get('/api/operations/boards').then(r => r.data)

export const getOperationsCards = (board) =>
  api.get('/api/operations/cards', { params: { board } }).then(r => r.data)

export const createOperationsCard = (data) =>
  api.post('/api/operations/cards', data).then(r => r.data)

export const updateOperationsCard = (id, data) =>
  api.patch(`/api/operations/cards/${id}`, data).then(r => r.data)

export const deleteOperationsCard = (id) =>
  api.delete(`/api/operations/cards/${id}`)

export const reorderOperationsCards = (data) =>
  api.post('/api/operations/cards/reorder', data).then(r => r.data)

// --- Billing ---
export const getBillingRates = () => api.get('/api/billing/rates').then(r => r.data)
export const updateBillingRate = (teamMemberId, data) =>
  api.put(`/api/billing/rates/${teamMemberId}`, data).then(r => r.data)
export const importBillingRates = (file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post('/api/billing/rates/import', form).then(r => r.data)
}
export const getMaterialCosts = (year, month) =>
  api.get('/api/billing/material-costs', { params: { year, month } }).then(r => r.data)
export const updateMaterialCost = (projectId, year, month, amountUsd) =>
  api.put(`/api/billing/material-costs/${projectId}`, { amount_usd: amountUsd }, { params: { year, month } }).then(r => r.data)
export const getBillingReport = (year, month) =>
  api.get('/api/billing/report', { params: { year, month } }).then(r => r.data)
export const downloadBillingReportPdf = async (year, month) => {
  const res = await api.get('/api/billing/report.pdf', {
    params: { year, month },
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `facturacion-${year}-${String(month).padStart(2, '0')}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// --- Notifications ---
export const getNotifications = (limit = 30) =>
  api.get('/api/notifications', { params: { limit } }).then(r => r.data)
export const getUnreadCount = () =>
  api.get('/api/notifications/unread-count').then(r => r.data)
export const markNotificationRead = (id) =>
  api.post(`/api/notifications/${id}/read`).then(r => r.data)
export const markAllNotificationsRead = () =>
  api.post('/api/notifications/read-all')

// --- Alerts (pop-up announcements) ---
export const getActiveAlerts = () =>
  api.get('/api/alerts/active').then(r => r.data)
export const getAlerts = () =>
  api.get('/api/alerts').then(r => r.data)
export const createAlert = (payload) =>
  api.post('/api/alerts', payload).then(r => r.data)
export const updateAlert = (id, payload) =>
  api.patch(`/api/alerts/${id}`, payload).then(r => r.data)
export const deleteAlert = (id) =>
  api.delete(`/api/alerts/${id}`)

// --- Warehouse Board ---
export const getWarehouseBoardRows = () =>
  api.get('/api/warehouse-board/rows').then(r => r.data)

export const createWarehouseBoardRow = (data = {}) =>
  api.post('/api/warehouse-board/rows', data).then(r => r.data)

export const updateWarehouseBoardRow = (id, data) =>
  api.patch(`/api/warehouse-board/rows/${id}`, data).then(r => r.data)

export const deleteWarehouseBoardRow = (id) =>
  api.delete(`/api/warehouse-board/rows/${id}`)

export const uploadWarehouseBoardAttachment = (rowId, file) => {
  const form = new FormData()
  form.append('file', file)
  return api.post(`/api/warehouse-board/rows/${rowId}/attachments`, form).then(r => r.data)
}

export const downloadWarehouseBoardAttachment = async (rowId, att) => {
  const res = await api.get(`/api/warehouse-board/rows/${rowId}/attachments/${att.id}`, {
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = att.filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const deleteWarehouseBoardAttachment = (rowId, attId) =>
  api.delete(`/api/warehouse-board/rows/${rowId}/attachments/${attId}`)

export default api

// --- Capacity (C-level) ---
// The cost password is sent per request, never traded for a token: the app has
// no signing secret of its own, and a per-process one breaks at random under
// more than one worker. It lives in React state only, so it dies on refresh --
// which is the behaviour a step-up check should have.
const costHeaders = (password) => (password ? { 'X-Cost-Password': password } : {})

// The committed staffing plan, one row per person and one column per project,
// over a run of `weeks` from the Monday of `start`.
export const getStaffingPlan = ({ weeks, start, costBasis, scenarioId }, password) =>
  api.get('/api/capacity/staffing-plan', {
    params: { weeks, start, cost_basis: costBasis, scenario_id: scenarioId || undefined },
    headers: costHeaders(password),
  }).then(r => r.data)

// One week of one person's dotacion on one project, edited from the weekly
// planning grid. Answers with what it actually stored: over the person's
// weekly ceiling is trimmed to it, not refused.
export const setStaffingCell = ({ projectId, teamMemberId, weekStart, hours, scenarioId }) =>
  api.put('/api/capacity/staffing-plan/cell', {
    project_id: projectId, team_member_id: teamMemberId,
    week_start: weekStart, hours, scenario_id: scenarioId || undefined,
  }).then(r => r.data)

// The same committed staffing plan, read by calendar month and by a
// discipline guessed from each person's own role/cargo text instead of by
// week and by person.
export const getStaffingMonthly = ({ year, month, count, scenarioId }) =>
  api.get('/api/capacity/staffing-monthly', {
    params: { year, month, count, scenario_id: scenarioId || undefined },
  }).then(r => r.data)

// Both shapes of the same plan: the project matrix and the weekly grid.
export const exportStaffingPlan = ({ weeks, start, costBasis }, password) =>
  api.get('/api/capacity/staffing-plan.xlsx', {
    params: { weeks, start, cost_basis: costBasis },
    headers: costHeaders(password), responseType: 'blob',
  }).then(res => _saveBlob(res.data, `hh_dotacion_${start}_${weeks}sem.xlsx`))

export const getCostAccessState = () =>
  api.get('/api/capacity/cost-access').then(r => r.data)

export const setCostPassword = (data) =>
  api.put('/api/capacity/cost-password', data).then(r => r.data)

export const getCostRates = (password) =>
  api.get('/api/capacity/rates', { headers: costHeaders(password) }).then(r => r.data)

export const setCostRates = (memberId, data, password) =>
  api.put(`/api/capacity/rates/${memberId}`, data, { headers: costHeaders(password) })
    .then(r => r.data)

// --- Weekly absences (frozen with the week, like its hours) ---
export const getWeekAbsences = (weekStart) =>
  api.get('/api/sent-weeks/absences', { params: { week_start: weekStart } })
    .then(r => r.data)

export const setWeekAbsence = (weekStart, data) =>
  api.put('/api/sent-weeks/absences', data, { params: { week_start: weekStart } })
    .then(r => r.data)

// --- HH activation report (monthly and quarterly, for accounting) ---
// The same cost password Capacity uses: company/client cost here is the same
// salary-adjacent figure, just priced by a different report.
export const getHHMonthlyReport = (year, month, costBasis, password) =>
  api.get('/api/hh-reports/monthly', {
    params: { year, month, cost_basis: costBasis }, headers: costHeaders(password),
  }).then(r => r.data)

export const exportHHMonthlyReport = (year, month, costBasis, password) =>
  api.get('/api/hh-reports/monthly.xlsx', {
    params: { year, month, cost_basis: costBasis }, headers: costHeaders(password),
    responseType: 'blob',
  }).then(res => _saveBlob(res.data,
    `hh_activacion_${year}_${String(month).padStart(2, '0')}.xlsx`))

export const getHHQuarterlyReport = (year, quarter, costBasis, password) =>
  api.get('/api/hh-reports/quarterly', {
    params: { year, quarter, cost_basis: costBasis }, headers: costHeaders(password),
  }).then(r => r.data)

export const exportHHQuarterlyReport = (year, quarter, costBasis, password) =>
  api.get('/api/hh-reports/quarterly.xlsx', {
    params: { year, quarter, cost_basis: costBasis }, headers: costHeaders(password),
    responseType: 'blob',
  }).then(res => _saveBlob(res.data, `hh_resumen_Q${quarter}_${year}.xlsx`))

export const getHHAnnualReport = (year, costBasis, password) =>
  api.get('/api/hh-reports/annual', {
    params: { year, cost_basis: costBasis }, headers: costHeaders(password),
  }).then(r => r.data)

export const exportHHAnnualReport = (year, costBasis, password) =>
  api.get('/api/hh-reports/annual.xlsx', {
    params: { year, cost_basis: costBasis }, headers: costHeaders(password),
    responseType: 'blob',
  }).then(res => _saveBlob(res.data, `hh_resumen_${year}.xlsx`))

export const getHHActivations = (year, month) =>
  api.get('/api/hh-reports/activations', { params: { year, month } }).then(r => r.data)

export const setHHActivation = (projectId, year, month, activated) =>
  api.put(`/api/hh-reports/activations/${projectId}`, { activated }, { params: { year, month } })
    .then(r => r.data)

// --- Client project tables (Cambio 4): per-engineer detail, gated by activation ---
export const getClientTablesMonthly = (year, month) =>
  api.get('/api/billing/client-tables/monthly', { params: { year, month } }).then(r => r.data)

export const exportClientTablesMonthly = (year, month) =>
  downloadXlsx(`/api/billing/client-tables/monthly.xlsx?year=${year}&month=${month}`,
    `tablas_cliente_${year}_${String(month).padStart(2, '0')}.xlsx`)

export const getClientTablesQuarterly = (year, quarter) =>
  api.get('/api/billing/client-tables/quarterly', { params: { year, quarter } }).then(r => r.data)

export const exportClientTablesQuarterly = (year, quarter) =>
  downloadXlsx(`/api/billing/client-tables/quarterly.xlsx?year=${year}&quarter=${quarter}`,
    `tablas_cliente_Q${quarter}_${year}.xlsx`)

// --- Weekly drill-down (which week a discrepancy comes from) ---
export const getWeekDetail = (year, month, { projectId, teamMemberId } = {}) =>
  api.get('/api/capacity/week-detail', {
    params: {
      year, month,
      project_id: projectId ?? undefined,
      team_member_id: teamMemberId ?? undefined,
    },
  }).then(r => r.data)

// The workspace the app opens on after signing in (null: the default).
export const setEspacioInicio = (espacio) => api.put('/api/auth/me/inicio', { espacio }).then(r => r.data)
// Órdenes de trabajo > Migración desde monday: the board's Excel export. aplicar=false simulates
// (nothing written) -> { aplicado, totales, filas }.
export const migrarMonday = (archivo, aplicar = false) => {
  const form = new FormData()
  form.append('archivo', archivo)
  form.append('aplicar', aplicar ? 'true' : 'false')
  return api.post('/api/ot/migracion-monday', form).then(r => r.data)
}
// Órdenes de trabajo: who can be put to work on an OT (areas and cargos), people's time on an OT,
// the per-person summary (dashboard) and the indicators.
export const getAsignablesOt = () => api.get('/api/ot/asignables').then(r => r.data)
export const putAsignablesOt = (data) => api.put('/api/ot/asignables', data).then(r => r.data)
export const getPersonasAsignables = () => api.get('/api/ot/asignables/personas').then(r => r.data)
export const getTrabajoOt = (otId) => api.get(`/api/ot/ordenes/${otId}/trabajo`).then(r => r.data)
export const iniciarTrabajoOt = (otId, data) => api.post(`/api/ot/ordenes/${otId}/trabajo/iniciar`, data).then(r => r.data)
export const detenerTrabajoOt = (id) => api.post(`/api/ot/trabajo/${id}/detener`).then(r => r.data)
export const getTableroTrabajoOt = () => api.get('/api/ot/trabajo/tablero').then(r => r.data)
export const getResumenTrabajo = (params) => api.get('/api/ot/trabajo/resumen', { params }).then(r => r.data)
export const getKpisOt = (params) => api.get('/api/ot/kpis', { params }).then(r => r.data)
// Mantenimiento > Carga masiva: tipo unidades | partes, monday's export. aplicar=false simulates and
// compares with what is there -> { tipo, aplicado, resumen, comparacion: { nuevos, modificados, sin_cambios } }.
export const cargaMasivaMant = (tipo, archivo, aplicar = false) => {
  const form = new FormData()
  form.append('archivo', archivo)
  form.append('aplicar', aplicar ? 'true' : 'false')
  return api.post(`/api/mantenimiento/carga/${tipo}`, form).then(r => r.data)
}

// --- Compras v2 > Pedidos SAP (SAP's order export, read-only here) ---
export const getComprasEstado = () => api.get('/api/compras/estado').then(r => r.data)
// Arrays go as repeated keys (?estado=a&estado=b), the way the server reads them.
export const getComprasPedidos = (params) => api.get('/api/compras/pedidos', { params, paramsSerializer: { indexes: null } }).then(r => r.data)
export const getComprasFiltros = () => api.get('/api/compras/pedidos/filtros').then(r => r.data)
export const getComprasPedido = (pedidoId) => api.get(`/api/compras/pedidos/${pedidoId}`).then(r => r.data)
export const getComprasImportaciones = () => api.get('/api/compras/importaciones').then(r => r.data)
// Preview writes nothing; the second one loads the same file.
const _subida = (url, file) => {
  const form = new FormData()
  form.append('archivo', file)
  return api.post(url, form).then(r => r.data)
}
export const previewComprasImportacion = (file) => _subida('/api/compras/importaciones/vista-previa', file)
export const confirmComprasImportacion = (file) => _subida('/api/compras/importaciones', file)
// The file of one of the last two loads, as it was uploaded.
export const descargarArchivoImportacion = async (id, nombre) => {
  const r = await api.get(`/api/compras/importaciones/${id}/archivo`, { responseType: 'blob' })
  const url = URL.createObjectURL(r.data)
  const a = document.createElement('a')
  a.href = url
  a.download = nombre || `carga-${id}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
// SAP suppliers (the last load), for the supplier suggestions of the purchase request form.
export const buscarProveedoresSap = (q, limit = 8) => api.get('/api/compras/catalogo/proveedores', { params: { q, limit } }).then(r => r.data)
// Excel of the orders: `ids` limits it to the ticked ones; without it, all that meet the filters.
export const exportComprasPedidos = async (params, ids) => {
  const res = await api.get('/api/compras/pedidos/exportar.xlsx', {
    params: { ...params, ...(ids?.length ? { ids } : {}) },
    paramsSerializer: { indexes: null },
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `pedidos_sap_${new Date().toISOString().slice(0, 10)}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Administración > Proyectos: Excel export of the ones on screen (or ticked) and import of a list.
export const exportAdminProjects = async (ids) => {
  const res = await api.get('/api/admin/projects/exportar.xlsx', {
    params: ids?.length ? { ids } : {}, paramsSerializer: { indexes: null }, responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = `proyectos_${new Date().toISOString().slice(0, 10)}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
const _subidaProyectos = (url, file) => {
  const form = new FormData()
  form.append('archivo', file)
  return api.post(url, form).then(r => r.data)
}
export const previewImportAdminProjects = (file) => _subidaProyectos('/api/admin/projects/importar/vista-previa', file)
export const importAdminProjects = (file) => _subidaProyectos('/api/admin/projects/importar', file)

// Compras: warning of a possible duplicate while a request is written (open requests + SAP orders).
export const revisarDuplicados = (lineas, excluirSolicitud) =>
  api.post('/api/compras/duplicados/revisar', { lineas, ...(excluirSolicitud ? { excluir_solicitud: excluirSolicitud } : {}) }).then(r => r.data)
export const registrarDecisionesDuplicado = (solicitudId, decisiones) =>
  api.post('/api/compras/duplicados/decisiones', { solicitud_id: solicitudId, decisiones }).then(r => r.data)

// Compras > Bandeja de compras (Compras and admin): the tasks, and the package to paste into a SAP order.
export const getComprasBandeja = () => api.get('/api/compras/bandeja').then(r => r.data)
export const getComprasPaquetes = (ids) =>
  api.get('/api/compras/bandeja/paquetes', { params: { ids }, paramsSerializer: { indexes: null } }).then(r => r.data)
// Joint purchase per supplier: Compras takes a request out of it (with a reason), puts it back, and marks
// what it loaded in SAP. `proveedorVigente` lists the live requests of a supplier, for whoever writes a new one.
export const separarSolicitudCompra = (id, motivo) =>
  api.post(`/api/compras/bandeja/solicitudes/${id}/separar`, { motivo }).then(r => r.data)
export const reincorporarSolicitudCompra = (id) =>
  api.post(`/api/compras/bandeja/solicitudes/${id}/reincorporar`).then(r => r.data)
export const cargarEnSapCompras = (ids, po_number) =>
  api.post('/api/compras/bandeja/cargar-en-sap', { ids, po_number: po_number || null }).then(r => r.data)
export const getFormatoSapSolicitud = (id) => api.get(`/api/compras/solicitudes/${id}/formato-sap`).then(r => r.data)
export const getProveedorVigente = (proveedor) =>
  api.get('/api/compras/duplicados/proveedor', { params: { proveedor } }).then(r => r.data)

// @mentions: who can be mentioned in a comment, each with the handle the picker inserts (@first_last).
export const getMencionables = () => api.get('/api/menciones/usuarios').then(r => r.data)

// Adquisiciones: import requests from Excel (preview writes nothing) and the template to fill in.
const _subidaSolicitudes = (url, file) => {
  const form = new FormData()
  form.append('archivo', file)
  return api.post(url, form).then(r => r.data)
}
export const previewImportPurchases = (file) => _subidaSolicitudes('/api/purchases/import/preview', file)
export const importPurchases = (file) => _subidaSolicitudes('/api/purchases/import', file)
export const downloadPurchasesTemplate = async () => {
  const res = await api.get('/api/purchases/import/plantilla.xlsx', { responseType: 'blob' })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = 'plantilla_solicitudes.xlsx'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
